import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTIVITY_STATUS_MAP,
  ACTIVITY_TYPE_MAP,
  CALL_DIRECTION_LABELS,
  MEETING_PROVIDER_LABELS,
  MESSAGE_CHANNEL_MAP,
  PRIORITY_DEFS,
  PRIORITY_MAP,
  type ActivityType,
  type FeedbackView,
  type MessageChannel,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { dateTime, plural, WORDS } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, ErrorState, Modal } from '../../components/ui';
import { OwnerChip, OwnerPickerModal } from '../../components/People';
import { Attachments } from '../../components/Attachments';
import { Feedback } from '../../components/Feedback';
import { TimeLog } from '../../components/WorkTimer';
import {
  RecordingPlayerModal,
  isAttachmentId,
  recordingKind,
} from '../../components/MeetingRecording';

/**
 * تفاصيل النشاط.
 *
 * النشاط كان عنوانًا وحالة وموعدًا في صفّ جدول، بينما المخطط يحمل له منذ
 * البداية اتجاه المكالمة ونتيجتها ومدتها، ومكان الاجتماع ومزوّده ورابطه
 * وحضوره، ومستلمي الرسالة. حقولٌ تُكتب ولا تُقرأ لأن لا شاشة تعرضها —
 * فيتوقف الناس عن كتابتها، ويصير السجل عنوانًا بلا مضمون.
 *
 * ولكل نوع ما يخصّه وحده: عرضُ «اتجاه المكالمة» على اجتماع يملأ الشاشة
 * بحقول فارغة تُعلَّم بتجاهلها.
 */

/*
 * لكلّ نوعٍ صياغتُه.
 *
 * كانت النافذة تقول «الملاحظات» فوق كل شيء وتصدّر عنوانها بـ«نشاط»:
 * فمحضر الاجتماع ونصّ الرسالة وما دار في المكالمة تُقرأ كلُّها بالاسم
 * نفسه. والتسمية الدقيقة تجعل القارئ يعرف ما بين يديه قبل أن يقرأه.
 */
const TITLE_PREFIX: Record<string, string> = {
  TASK: 'مهمة',
  MEETING: 'اجتماع',
  CALL: 'مكالمة',
  MAIL: 'رسالة',
  NOTE: 'ملاحظة',
};

const BODY_HEADINGS: Record<string, string> = {
  TASK: 'وصف المهمة',
  MEETING: 'جدول الأعمال والمحضر',
  CALL: 'ما دار في المكالمة',
  MAIL: 'نصّ الرسالة',
  NOTE: 'نصّ الملاحظة',
};

interface ActivityFull {
  id: string;
  type: ActivityType;
  status: 'PLANNED' | 'DONE' | 'CANCELLED';
  priority: 'HIGH' | 'NORMAL' | 'LOW';
  subject: string;
  body: string | null;
  dueAt: string | null;
  completedAt: string | null;
  durationMin: number | null;
  ownerId: string | null;
  assigneeId: string | null;
  location: string | null;
  meetingProvider: string | null;
  meetingUrl: string | null;
  /** موقع الانعقاد على الخريطة — زرُّ ملاحةٍ للحضوريّ. */
  mapsUrl: string | null;
  /** مهمة اللوح المزروعة من هذه المهمة، ومشروعُها — بهما يُفتح لوحُها. */
  agencyTaskId: string | null;
  agencyProjectId: string | null;
  /** رابط تسجيل الاجتماع — يُلحق بعد الإنجاز ويُشغَّل من الملف. */
  recordingUrl: string | null;
  attendees: string[] | null;
  callDirection: string | null;
  callOutcome: string | null;
  mailTo: string | null;
  mailCc: string | null;
  mailSubject: string | null;
  messageChannel: string | null;
  noteAbout: string | null;
  createdAt: string;
  account: { id: string; name: string } | null;
  lead: { id: string; name: string } | null;
  deal: { id: string; title: string } | null;
  contact: { id: string; name: string } | null;
}

export function ActivityDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [assigning, setAssigning] = useState(false);
  /* إعادة فتح المنجَز — نافذةٌ تطلب كلمة سرّ الحماية، والخادم هو الحكم. */
  /* دربُ العودة: من فتح النشاط داخل ملف عميلٍ يعود إليه من لوح المشروع. */
  const { pathname } = useLocation();
  const backToClient =
    pathname.startsWith('/crm/leads/') || pathname.startsWith('/crm/accounts/')
      ? pathname
      : null;
  const [reopening, setReopening] = useState(false);
  const [reopenPass, setReopenPass] = useState('');
  const [reopenErr, setReopenErr] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['activity', id],
    queryFn: () => api<ActivityFull>(`/crm/activities/${id}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['activity', id] });
    void queryClient.invalidateQueries({ queryKey: ['activities'] });
    void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    void queryClient.invalidateQueries({ queryKey: ['agency'] });
  };

  const reopen = useMutation({
    mutationFn: () =>
      api(`/crm/activities/${id}/reopen`, { method: 'POST', body: { password: reopenPass } }),
    onSuccess: () => {
      setReopening(false);
      setReopenPass('');
      setReopenErr(null);
      refresh();
    },
    onError: (e) => setReopenErr(e instanceof Error ? e.message : 'تعذّرت إعادة الفتح.'),
  });

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/crm/activities/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      setAssigning(false);
      refresh();
    },
  });

  const complete = useMutation({
    mutationFn: () => api(`/crm/activities/${id}/complete`, { method: 'POST', body: {} }),
    onSuccess: refresh,
  });

  const a = q.data;
  const type = a ? ACTIVITY_TYPE_MAP[a.type] : undefined;

  return (
    <Modal
      title={a ? `${TITLE_PREFIX[a.type] ?? type?.label ?? 'نشاط'}: ${a.subject}` : 'نشاط'}
      onClose={onClose}
      wide
      footer={
        a && (
          <>
            <button className="btn" onClick={onClose}>
              إغلاق
            </button>

            {/*
              المهمة تُدار في لوحها: زرٌّ يفتح مشروعها في إدارة المشاريع
              حيث أقسامُها وتعليقاتُها وتبعيّاتُها — لا نسخةٌ ثانيةٌ هنا.
            */}
            {a.type === 'TASK' && a.agencyTaskId && a.agencyProjectId && (
              <Link
                className="btn"
                to={`/agency/projects/${a.agencyProjectId}?task=${a.agencyTaskId}${
                  backToClient ? `&backTo=${encodeURIComponent(backToClient)}` : ''
                }`}
                title="افتح المهمة في إدارة المشاريع"
              >
                <Icon name="rocket" size={15} />
                افتحها في المشاريع
              </Link>
            )}

            {/* المنجَز يُعاد فتحه بكلمة سرّ الحماية — تجميدٌ لا سجن. */}
            {a.status === 'DONE' && (
              <button className="btn" onClick={() => setReopening(true)}>
                <Icon name="refresh" size={15} />
                إعادة فتحه
              </button>
            )}

            {a.status === 'PLANNED' && (
              <button
                className="btn btn-primary"
                onClick={() => complete.mutate()}
                disabled={complete.isPending}
              >
                <Icon name="check" size={15} />
                {complete.isPending ? 'جارٍ الإنهاء' : 'تعليم كمنجَز'}
              </button>
            )}
          </>
        )
      }
    >
      {q.isPending && <div className="skeleton" style={{ height: 220 }} />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}

      {patch.isError && (
        <div className="alert alert-danger mb-4">
          {patch.error instanceof Error ? patch.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {/*
        الملاحظة ملاحظةٌ فعلًا لا نشاط: موضوعها في العنوان ونصّها تحته
        وتاريخُ كتابتها — لا حالة ولا أولوية ولا مسؤول ولا موعد، فتلك
        عُدّة عملٍ يُتابَع، والملاحظة قيلت وانتهى.
      */}
      {a && a.type === 'NOTE' && (
        <>
          <div className="row mb-4" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <span className="tiny mute">{dateTime(a.createdAt)}</span>
            {a.noteAbout && ACTIVITY_TYPE_MAP[a.noteAbout as ActivityType] && (
              <Badge>تخصّ {ACTIVITY_TYPE_MAP[a.noteAbout as ActivityType]?.plural}</Badge>
            )}
            <LinkedRecord activity={a} />
          </div>
          {a.body ? (
            <p className="tl-detail">{a.body}</p>
          ) : (
            <p className="mute small">لا نصّ — الموضوع وحده.</p>
          )}
        </>
      )}

      {a && a.type !== 'NOTE' && (
        <>
          <div className="row mb-4" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <Badge kind={ACTIVITY_STATUS_MAP[a.status]?.kind}>
              {ACTIVITY_STATUS_MAP[a.status]?.label}
            </Badge>
            <Badge kind={PRIORITY_MAP[a.priority]?.kind}>{PRIORITY_MAP[a.priority]?.label}</Badge>
            <LinkedRecord activity={a} />
          </div>

          <dl className="detail-list">
            <dt>المسؤول</dt>
            <dd>
              <span className="row" style={{ gap: 8 }}>
                <OwnerChip ownerId={a.assigneeId} size={22} />
                <button className="btn btn-sm btn-ghost" onClick={() => setAssigning(true)}>
                  تغيير
                </button>
              </span>
            </dd>

            <dt>الموعد</dt>
            <dd>{a.dueAt ? dateTime(a.dueAt) : 'بلا موعد'}</dd>

            {a.completedAt && (
              <>
                <dt>أُنجز في</dt>
                <dd>{dateTime(a.completedAt)}</dd>
              </>
            )}

            {/* المدة تُعرض لما له مدة: المهمة لا مدة لها بمعنى مفيد. */}
            {(a.type === 'CALL' || a.type === 'MEETING') && (
              <>
                <dt>المدة</dt>
                <dd>{a.durationMin ? `${a.durationMin} دقيقة` : '—'}</dd>
              </>
            )}

            {a.type === 'CALL' && (
              <>
                <dt>الاتجاه</dt>
                <dd>
                  {a.callDirection
                    ? CALL_DIRECTION_LABELS[a.callDirection as 'OUTBOUND' | 'INBOUND']
                    : '—'}
                </dd>
                <dt>النتيجة</dt>
                <dd>{a.callOutcome ?? '—'}</dd>
              </>
            )}

            {a.type === 'MEETING' && (
              <>
                <dt>الوسيلة</dt>
                <dd>
                  {a.meetingProvider
                    ? MEETING_PROVIDER_LABELS[
                        a.meetingProvider as keyof typeof MEETING_PROVIDER_LABELS
                      ]
                    : '—'}
                </dd>
                <dt>المكان</dt>
                <dd>
                  <span className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    {a.location ?? '—'}
                    {/* الموقع زرُّ ملاحةٍ يُنقر — لا عنوانٌ يُنسخ إلى الخرائط. */}
                    {a.mapsUrl && (
                      <a
                        className="btn btn-sm act-nav"
                        href={a.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="افتح الموقع في الخرائط"
                      >
                        <Icon name="mapPin" size={13} />
                        الملاحة إلى المكان
                      </a>
                    )}
                  </span>
                </dd>
                <dt>الرابط</dt>
                <dd>
                  {a.meetingUrl ? (
                    /*
                     * `noreferrer` مع `noopener`: الرابط يأتي من إدخال
                     * مستخدم، فلا يُمنح الصفحةَ المفتوحة مرجعًا إلى نافذتنا
                     * ولا اسمَ الصفحة التي جاء منها.
                     */
                    <a href={a.meetingUrl} target="_blank" rel="noopener noreferrer">
                      فتح الاجتماع
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
                <dt>الحضور</dt>
                <dd>
                  {a.attendees?.length
                    ? `${plural(a.attendees.length, WORDS.member)}: ${a.attendees.join('، ')}`
                    : '—'}
                </dd>
              </>
            )}

            {a.type === 'MAIL' && (
              <>
                <dt>القناة</dt>
                <dd>
                  {MESSAGE_CHANNEL_MAP[(a.messageChannel ?? 'EMAIL') as MessageChannel]?.label ??
                    a.messageChannel}
                </dd>
                <dt>الموضوع</dt>
                <dd>{a.mailSubject ?? '—'}</dd>
                <dt>إلى</dt>
                <dd className="num" dir="ltr">
                  {a.mailTo ?? '—'}
                </dd>
                <dt>نسخة</dt>
                <dd className="num" dir="ltr">
                  {a.mailCc ?? '—'}
                </dd>
              </>
            )}

            {/*
              الأولوية حكمٌ يُقال عند الإنشاء ويُقرأ بعده.
              تبديلُها لاحقًا يُعيد كتابة تاريخ القرار: ما كان عاجلًا فأُهمل
              يصير «عاديًّا» بنقرةٍ فلا يبقى أثرٌ للتقصير. من أخطأ يُلغي
              وينشئ بديلًا مصرَّحًا به.
            */}
            <dt>الأولوية</dt>
            <dd>
              <Badge kind={PRIORITY_MAP[a.priority]?.kind}>{PRIORITY_MAP[a.priority]?.label}</Badge>
            </dd>
          </dl>

          {/*
            تسجيل الاجتماع يُشغَّل في مكانه: الرابط يُلصق في ملاحظات
            النشاط بعد الاجتماع، والقسم يلتقطه ويفتحه في مشغّلٍ داخل
            النظام — لا رحلة إلى تبويبٍ آخر لمشاهدة ما جرى.
          */}
          {/*
            وتسجيلُ المكالمة كذلك — محفوظٌ عندنا مرفقًا لا رابطًا عند
            منصّة، فيُسمع هنا حيث تُقرأ نتيجتُها وما دار فيها. ولا يُعرض
            القسمُ لمكالمةٍ بلا تسجيل: بابُ لصقِ رابطٍ لا معنى له هنا.
          */}
          {a.type === 'CALL' && a.recordingUrl && <MeetingRecordings activity={a} />}

          {a.body && (
            <div className="mt-4">
              {/* لكلّ نوعٍ لغتُه: محضرُ اجتماعٍ ليس «ملاحظات» ولا نصَّ رسالة. */}
              <h3 className="card-title">{BODY_HEADINGS[a.type] ?? 'الملاحظات'}</h3>
              <p className="tl-detail">{a.body}</p>
            </div>
          )}

          {/*
            المرفقات على النشاط نفسه.
            محضر الاجتماع وتسجيل المكالمة يخصّان النشاط لا العميل: إرفاقهما
            بالعميل يخلطهما بمحاضر عشرة اجتماعات أخرى بلا ما يميّزها.
          */}
          <div className="mt-4">
            <Attachments entity="activity" entityId={a.id} title="مرفقات النشاط" />
          </div>

          <div className="mt-4">
            <Feedback entity="activity" entityId={a.id} title="ملاحظات على ما جرى" />
          </div>

          <div className="mt-4">
            <TimeLog entity="activity" entityId={a.id} title="الوقت على هذا النشاط" />
          </div>

          {/*
            إعادة الفتح ببوّابةٍ واحدة: كلمة سرّ الحماية نفسها التي تحرس
            محو المنجَز. والرسالة تقول ما سيقع — تاريخُ الإنجاز يُمحى.
          */}
          {reopening && (
            <Modal
              title="إعادة فتح المنجَز"
              onClose={() => {
                setReopening(false);
                setReopenErr(null);
              }}
              footer={
                <>
                  <button className="btn" onClick={() => setReopening(false)}>
                    إلغاء
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!reopenPass || reopen.isPending}
                    onClick={() => reopen.mutate()}
                  >
                    {reopen.isPending ? 'جارٍ الفتح' : 'أعد فتحه'}
                  </button>
                </>
              }
            >
              {reopenErr && <div className="alert alert-danger mb-4">{reopenErr}</div>}
              <p className="small mb-4">
                سيعود هذا النشاط «مجدولًا» ويُمحى تاريخ إنجازه — ومهمتُه في اللوح تعود
                مفتوحةً معه. اكتب كلمة سرّ الحماية للمتابعة.
              </p>
              <div className="field">
                <label className="label" htmlFor="reopen-pass">
                  كلمة سرّ الحماية
                </label>
                <input
                  id="reopen-pass"
                  className="input"
                  type="password"
                  value={reopenPass}
                  onChange={(e) => setReopenPass(e.target.value)}
                  autoFocus
                />
              </div>
            </Modal>
          )}

          {assigning && (
            <OwnerPickerModal
              currentId={a.assigneeId}
              title="مسؤول النشاط"
              pending={patch.isPending}
              error={patch.error}
              onPick={(userId) => patch.mutate({ assigneeId: userId })}
              onClose={() => setAssigning(false)}
            />
          )}
        </>
      )}
    </Modal>
  );
}

/* ==========================================================================
   تسجيل الاجتماع — حقل الرابط المخصّص وروابط الملاحظات، تُشغَّل في مكانها
   ========================================================================== */

interface RecordingLink {
  kind: 'file' | 'embed' | 'external';
  src: string;
  url: string;
  by: string | null;
  at: string;
}

function MeetingRecordings({ activity }: { activity: ActivityFull }) {
  const queryClient = useQueryClient();
  const activityId = activity.id;
  const [playing, setPlaying] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(activity.recordingUrl ?? '');
  const [saveErr, setSaveErr] = useState<string | null>(null);

  /*
   * الرابط المخصّص يُحفظ على النشاط نفسه — والخادم يسمح بإلحاقه بعد
   * الإنجاز وحده دون سائر الحقول: التسجيل يصل بعد وقوع الاجتماع.
   */
  const save = useMutation({
    mutationFn: (recordingUrl: string | null) =>
      api(`/crm/activities/${activityId}`, { method: 'PATCH', body: { recordingUrl } }),
    onSuccess: () => {
      setEditing(false);
      setSaveErr(null);
      void queryClient.invalidateQueries({ queryKey: ['activity', activityId] });
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
    onError: (e) => setSaveErr(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const saved = activity.recordingUrl;
  const savedKind = saved ? recordingKind(saved) : null;
  /*
   * العمودُ واحدٌ يحمل نوعين: رابطَ اجتماعٍ عند منصّته، ومرفقَ مكالمةٍ
   * عندنا. فيُسمّى كلٌّ باسمه، ولا يُعرض «تعديل الرابط» لما ليس برابط.
   */
  const savedIsCall = Boolean(saved && isAttachmentId(saved));

  /* مفتاح مكوّن الملاحظات نفسه — الكاش واحد فلا طلب مكرّرًا. */
  const fb = useQuery({
    queryKey: ['feedback', 'activity', activityId],
    queryFn: () =>
      api<FeedbackView[]>(`/feedback${qs({ entity: 'activity', entityId: activityId })}`),
  });

  const links: RecordingLink[] = [];
  const seen = new Set<string>();
  for (const item of (fb.data ?? []).flatMap((r) => [r, ...r.replies])) {
    for (const m of (item.body ?? '').matchAll(/https?:\/\/[^\s)>\]»،]+/g)) {
      const v = recordingKind(m[0]);
      if (v && !seen.has(v.src)) {
        seen.add(v.src);
        links.push({ ...v, url: m[0], by: item.authorName, at: item.createdAt });
      }
    }
  }

  return (
    <div className="mt-4">
      <h3 className="card-title">
        {savedIsCall ? 'تسجيل المكالمة' : 'تسجيل الاجتماع'}
      </h3>

      {saveErr && <div className="alert alert-danger mt-2">{saveErr}</div>}

      {/* المكان المخصّص: يُلصق فيه الرابط فقط — ويُشغَّل من كل مكان. */}
      {editing ? (
        <div className="row mt-2" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          <input
            className="input num"
            dir="ltr"
            style={{ flex: 1, minWidth: 220 }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim() && !save.isPending) save.mutate(draft.trim());
            }}
            placeholder="https://…"
            autoFocus
          />
          <button
            className="btn btn-sm btn-primary"
            disabled={save.isPending || !draft.trim()}
            onClick={() => save.mutate(draft.trim())}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
          <button
            className="btn btn-sm"
            onClick={() => {
              setEditing(false);
              setDraft(saved ?? '');
            }}
          >
            إلغاء
          </button>
        </div>
      ) : saved ? (
        <div className="cc-recent-row mt-2">
          <span style={{ display: 'inline-flex', color: 'var(--brand)' }}>
            <Icon name="video" size={15} />
          </span>
          {savedKind?.kind === 'external' ? (
            /* منصّته تمنع التضمين — يُفتح عندها بصراحة. */
            <a
              className="cc-rowbtn cc-recent-who link"
              href={saved}
              target="_blank"
              rel="noopener noreferrer"
              title="المنصّة لا تسمح بالتشغيل داخل النظام — يُفتح عندها"
            >
              تسجيل الاجتماع — يُفتح على المنصّة
            </a>
          ) : (
            <button
              type="button"
              className="cc-rowbtn cc-recent-who"
              onClick={() => setPlaying(saved)}
              title="شغّل التسجيل هنا"
            >
              {savedIsCall ? 'تشغيل تسجيل المكالمة' : 'تشغيل تسجيل الاجتماع'}
            </button>
          )}
          <span className="row" style={{ marginInlineStart: 'auto', gap: 4 }}>
            {!savedIsCall && (
              <button
                className="link-btn tiny"
                onClick={() => {
                  setDraft(saved);
                  setEditing(true);
                }}
              >
                تعديل
              </button>
            )}
            <button
              className="link-btn tiny link-danger"
              disabled={save.isPending}
              onClick={() => save.mutate(null)}
            >
              إزالة
            </button>
          </span>
        </div>
      ) : (
        <button type="button" className="btn btn-sm mt-2" onClick={() => setEditing(true)}>
          <Icon name="video" size={14} />
          رابط تسجيل الاجتماع — اضغط والصق الرابط
        </button>
      )}

      {/* وما لُصق في الملاحظات يُلتقط كذلك — مصدرٌ ثانٍ لا بديل. */}
      {links.length > 0 && (
        <div className="col mt-2" style={{ gap: 'var(--sp-1)' }}>
          {links
            .filter((l) => l.url !== saved)
            .map((l) => (
              <div key={l.src} className="cc-recent-row">
                <span style={{ display: 'inline-flex', color: 'var(--brand)' }}>
                  <Icon name="video" size={15} />
                </span>
                {l.kind === 'external' ? (
                  <a
                    className="cc-rowbtn cc-recent-who link"
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="المنصّة لا تسمح بالتشغيل داخل النظام — يُفتح عندها"
                  >
                    تسجيل من الملاحظات — يُفتح على المنصّة
                  </a>
                ) : (
                  <button
                    type="button"
                    className="cc-rowbtn cc-recent-who"
                    onClick={() => setPlaying(l.url)}
                    title="شغّل التسجيل هنا"
                  >
                    تشغيل تسجيل من الملاحظات
                  </button>
                )}
                <span className="tiny mute nowrap" style={{ marginInlineStart: 'auto' }}>
                  {l.by ?? ''} · {dateTime(l.at)}
                </span>
              </div>
            ))}
        </div>
      )}

      {playing && <RecordingPlayerModal url={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}

/** السجل المرتبط، أيًّا كان نوعه. */
function LinkedRecord({ activity }: { activity: ActivityFull }) {
  if (activity.lead) {
    return (
      <Link to={`/crm/leads/${activity.lead.id}`} className="tiny">
        <Icon name="funnel" size={13} /> {activity.lead.name}
      </Link>
    );
  }
  if (activity.account) {
    return (
      <Link to={`/crm/accounts/${activity.account.id}`} className="tiny">
        <Icon name="building" size={13} /> {activity.account.name}
      </Link>
    );
  }
  if (activity.deal) {
    return (
      <span className="tiny mute">
        <Icon name="chart" size={13} /> {activity.deal.title}
      </span>
    );
  }
  return null;
}
